import { useState, useEffect } from "react";

const BASE = "https://imalishawelfare.com/api";
const getToken = () => localStorage.getItem("token");

async function req(endpoint, options = {}) {
  const token = getToken();
  const res = await fetch(BASE + endpoint, {
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: "Bearer " + token } : {}), ...options.headers },
    ...options,
  });
  let data; try { data = await res.json(); } catch { data = {}; }
  if (!res.ok) throw new Error(data.message || "Error " + res.status);
  return data;
}

function fmt(n) { return "KSh " + Number(n).toLocaleString("en-KE"); }

export default function LoanDashboard({ applicationId, phone }) {
  const [offer, setOffer]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(0);
  const [step, setStep]         = useState("select");
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState("");
  const [toast, setToast]       = useState("");
  const [payPhone, setPayPhone] = useState(phone||"");

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(""), 4000); };

  useEffect(() => {
    req("/loans/offer/" + applicationId)
      .then(data => {
        setOffer(data);
        if (!payPhone && data.phone) setPayPhone(data.phone);
        if (data.loanStatus === "DISBURSED") {
          setStep("paid");
        } else if (data.loanStatus === "FEE_PAID") {
          setStep("fee_paid");
        } else if (data.loanStatus === "PAYMENT_FAILED") {
          setError("Your last payment failed. Please try again.");
          setStep("select");
        } else if (data.loanStatus === "PENDING_FEE" && data.selectedAmount) {
          setSelected(data.selectedAmount);
          setStep("pending");
        }
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [applicationId]);

  const fee = selected ? Math.ceil(selected * 0.15) : 0;
  const net = selected ? selected - fee : 0;

  const handlePayFee = async () => {
    setBusy(true); setError("");
    try {
      const usePhone = payPhone && payPhone.length === 10 ? payPhone : phone;
      await req("/loans/select-amount", { method: "POST", body: JSON.stringify({ applicationId, loanAmount: selected, phone: usePhone }) });
      setStep("pending");
      showToast("M-Pesa prompt sent to " + usePhone + "! Enter PIN to complete.");
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "var(--text-secondary)" }}>Loading loan offer...</div>;
  if (!offer) return <div style={{ padding: 20, background: "#fee2e2", borderRadius: 10, color: "#7f1d1d", fontSize: 13 }}>{error || "No loan offer found"}</div>;

  const quickAmounts = [10000, 15000, 20000, 30000, 40000, 50000].filter(v => v >= offer.minAmount && v <= offer.maxAmount);

  return (
    <div>
      {toast && <div style={{ position: "fixed", top: 20, right: 20, zIndex: 9999, background: "#1f2937", color: "white", padding: "12px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600 }}>{toast}</div>}

      {step === "select" && (
        <div>
          <div style={{ background: "linear-gradient(135deg,#0066cc,#0052a3)", borderRadius: 14, padding: "28px 24px", color: "white", marginBottom: 20, textAlign: "center" }}>
            <p style={{ fontSize: 12, opacity: .75, margin: "0 0 6px", textTransform: "uppercase", letterSpacing: 1 }}>Your Approved Loan Offer</p>
            <h2 style={{ fontSize: 28, fontWeight: 900, margin: "0 0 4px" }}>{fmt(offer.minAmount)} - {fmt(offer.maxAmount)}</h2>
            <p style={{ fontSize: 13, opacity: .8, margin: 0 }}>Choose any amount within your approved range</p>
          </div>

          <div style={{ background: "white", borderRadius: 14, padding: 24, marginBottom: 16, boxShadow: "0 2px 8px rgba(0,0,0,0.07)", border: "1px solid var(--border-light)" }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", margin: "0 0 14px", textTransform: "uppercase", letterSpacing: .5 }}>Quick Select</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 20 }}>
              {quickAmounts.map(v => (
                <button key={v} onClick={() => setSelected(v)} style={{ padding: "10px 8px", borderRadius: 8, border: "2px solid " + (selected === v ? "var(--primary-color)" : "var(--border-light)"), background: selected === v ? "var(--primary-light)" : "white", fontWeight: 700, fontSize: 13, cursor: "pointer", color: selected === v ? "var(--primary-color)" : "var(--text-dark)" }}>
                  {fmt(v)}
                </button>
              ))}
            </div>

            <p style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", margin: "0 0 8px", textTransform: "uppercase", letterSpacing: .5 }}>Custom Amount</p>
            <div style={{ position: "relative", marginBottom: 20 }}>
              <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontSize: 13, fontWeight: 700, color: "var(--text-secondary)" }}>KSh</span>
              <input type="number" value={selected || ""} onChange={e => setSelected(Number(e.target.value))}
                min={offer.minAmount} max={offer.maxAmount} step={1000}
                style={{ width: "100%", padding: "12px 14px 12px 52px", border: "1.5px solid var(--border-light)", borderRadius: 8, fontSize: 15, fontWeight: 700, boxSizing: "border-box" }}
                placeholder={offer.minAmount + " - " + offer.maxAmount}/>
            </div>

            {selected > 0 && (
              <div style={{ background: "var(--bg-light)", borderRadius: 10, padding: 16, border: "1px solid var(--border-light)", marginBottom: 16 }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: "var(--text-secondary)", margin: "0 0 10px", textTransform: "uppercase", letterSpacing: .5 }}>Loan Breakdown</p>
                {[["Loan Amount", fmt(selected), "#1f2937"], ["Processing Fee (15%)", "- " + fmt(fee), "#ef4444"], ["You Receive", fmt(net), "#10b981"]].map(([l, v, c]) => (
                  <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--border-light)", fontSize: 13 }}>
                    <span style={{ color: "var(--text-secondary)" }}>{l}</span>
                    <span style={{ fontWeight: 800, color: c }}>{v}</span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 10, fontSize: 14 }}>
                  <span style={{ fontWeight: 700 }}>Pay via M-Pesa</span>
                  <span style={{ fontWeight: 900, color: "#0066cc", fontSize: 16 }}>{fmt(fee)}</span>
                </div>
              </div>
            )}

            {error && <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#7f1d1d" }}>{error}</div>}

            <button onClick={() => { if (!selected || selected < offer.minAmount || selected > offer.maxAmount) { setError("Enter amount between " + fmt(offer.minAmount) + " and " + fmt(offer.maxAmount)); return; } setStep("confirm"); }}
              disabled={!selected}
              style={{ width: "100%", padding: 14, background: selected ? "linear-gradient(135deg,#0066cc,#0052a3)" : "var(--border-light)", color: "white", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: selected ? "pointer" : "default" }}>
              Proceed to Payment
            </button>
          </div>
        </div>
      )}

      {step === "confirm" && (
        <div style={{ background: "white", borderRadius: 14, padding: 28, boxShadow: "0 2px 8px rgba(0,0,0,0.07)", border: "1px solid var(--border-light)" }}>
          <h3 style={{ fontSize: 18, fontWeight: 800, margin: "0 0 20px", textAlign: "center" }}>Confirm Payment</h3>
          <div style={{ background: "var(--bg-light)", borderRadius: 10, padding: 18, marginBottom: 20, border: "1px solid var(--border-light)" }}>
            {[["Loan Amount", fmt(selected)], ["Processing Fee (15%)", fmt(fee)], ["Amount to Receive", fmt(net)], ["Payment Phone", phone], ["Method", "M-Pesa STK Push"]].map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border-light)", fontSize: 13 }}>
                <span style={{ color: "var(--text-secondary)" }}>{k}</span>
                <span style={{ fontWeight: 700 }}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{ marginBottom:16 }}>
            <p style={{ fontSize:12, fontWeight:700, color:"#64748b", margin:"0 0 6px", textTransform:"uppercase", letterSpacing:.5 }}>M-Pesa Number to Charge</p>
            <div style={{ position:"relative" }}>
              <input
                type="tel"
                value={payPhone}
                onChange={e => setPayPhone(e.target.value.replace(/\D/g,""))}
                maxLength={10}
                placeholder="e.g. 0712345678"
                style={{ width:"100%", padding:"12px 14px", border:"1.5px solid #e2e8f0", borderRadius:8, fontSize:15, fontWeight:600, boxSizing:"border-box", fontFamily:"monospace" }}
              />
            </div>
            <p style={{ fontSize:11, color:"#94a3b8", margin:"4px 0 0" }}>Default is your registered number. You can change it to pay from a different M-Pesa.</p>
          </div>
          <div style={{ background:"#fff8e6", border:"1px solid #f59e0b", borderRadius:8, padding:"12px 16px", marginBottom:20, fontSize:13, color:"#92400e" }}>
            An M-Pesa prompt will be sent to <strong>{payPhone||phone}</strong>. Enter your PIN to pay <strong>{fmt(fee)}</strong>.
          </div>
          {error && <div style={{ background: "#fee2e2", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#7f1d1d" }}>{error}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <button onClick={() => setStep("select")} style={{ padding: 13, background: "white", color: "var(--primary-color)", border: "2px solid var(--primary-color)", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>Back</button>
            <button onClick={handlePayFee} disabled={busy} style={{ padding: 13, background: "linear-gradient(135deg,#10b981,#059669)", color: "white", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer", opacity: busy ? 0.6 : 1 }}>
              {busy ? "Sending..." : "Pay Now"}
            </button>
          </div>
        </div>
      )}

      {step === "pending" && (
        <div style={{ background: "white", borderRadius: 14, padding: "32px 24px", boxShadow: "0 2px 8px rgba(0,0,0,0.07)", textAlign: "center", border: "1px solid var(--border-light)" }}>
          <div style={{ width:72, height:72, background:"#eff6ff", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px" }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#0066cc" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12" y2="18"/></svg>
          </div>
          <h3 style={{ fontSize: 20, fontWeight: 800, margin: "0 0 10px" }}>Check Your Phone</h3>
          <p style={{ color: "var(--text-secondary)", fontSize: 14, lineHeight: 1.7, marginBottom: 16 }}>
            M-Pesa prompt sent to <strong>{phone}</strong>.<br/>Enter PIN to pay <strong style={{ color: "#0066cc" }}>{fmt(fee || Math.ceil((selected||offer.selectedAmount||0)*0.15))}</strong>.
          </p>
          <div style={{ background: "#ecfdf5", border: "1px solid #6ee7b7", borderRadius: 8, padding: 14, fontSize: 13, color: "#065f46", marginBottom: 20 }}>
            Your loan of <strong>{fmt(selected || offer.selectedAmount)}</strong> will be disbursed within 24 hours after payment.
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
            <button onClick={async () => {
              setBusy(true); setError("");
              try {
                const updated = await req("/loans/offer/" + applicationId);
                if (updated.loanStatus === "FEE_PAID" || updated.loanStatus === "DISBURSED") {
                  setStep("paid");
                } else {
                  showToast("Payment not confirmed yet. Please check your M-Pesa messages.");
                }
              } catch(e) { setError(e.message); } finally { setBusy(false); }
            }} disabled={busy} style={{ padding:"11px", background:"var(--primary-color)", color:"white", border:"none", borderRadius:8, fontWeight:700, fontSize:13, cursor:"pointer" }}>
              {busy ? "Checking..." : "Check Status"}
            </button>
            <button onClick={async () => {
              setBusy(true); setError("");
              try {
                const usePhone = payPhone && payPhone.length === 10 ? payPhone : phone;
                await req("/loans/select-amount", { method:"POST", body:JSON.stringify({ applicationId, loanAmount: selected||offer.selectedAmount, phone: usePhone }) });
                showToast("New M-Pesa prompt sent to " + usePhone + "!");
              } catch(e) { setError(e.message); } finally { setBusy(false); }
            }} disabled={busy} style={{ padding:"11px", background:"linear-gradient(135deg,#10b981,#059669)", color:"white", border:"none", borderRadius:8, fontWeight:700, fontSize:13, cursor:"pointer" }}>
              {busy ? "Sending..." : "Resend Prompt"}
            </button>
          </div>
          <button onClick={() => { setStep("select"); setError(""); }} style={{ width:"100%", padding:"10px", background:"white", color:"#64748b", border:"1.5px solid #e2e8f0", borderRadius:8, fontWeight:600, fontSize:13, cursor:"pointer" }}>
            Change Amount
          </button>
          {error && <div style={{ background:"#fee2e2", borderRadius:8, padding:"10px 14px", marginTop:12, fontSize:13, color:"#7f1d1d" }}>{error}</div>}
        </div>
      )}

      {step === "fee_paid" && (
        <div style={{ background:"white", borderRadius:14, padding:"40px 28px", textAlign:"center", boxShadow:"0 2px 8px rgba(0,0,0,0.07)" }}>
          <div style={{ width:72, height:72, background:"#eff6ff", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px" }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#0066cc" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <h3 style={{ fontSize:20, fontWeight:800, color:"#0052a3", margin:"0 0 10px" }}>Payment Received!</h3>
          <p style={{ color:"#64748b", fontSize:14, lineHeight:1.7, marginBottom:20 }}>
            Your processing fee of <strong style={{ color:"#0066cc" }}>{fmt(Math.ceil((offer.selectedAmount||0)*0.15))}</strong> has been received.<br/>
            Your loan of <strong style={{ color:"#10b981" }}>{fmt(offer.selectedAmount)}</strong> is being processed.
          </p>
          <div style={{ background:"#f0fdf4", border:"1px solid #86efac", borderRadius:10, padding:"14px 18px", marginBottom:20, fontSize:13, color:"#166534" }}>
            <strong>What happens next?</strong><br/>
            Our team will verify your payment and disburse your loan within <strong>24 hours</strong>. You will receive an SMS confirmation once funds are sent.
          </div>
          <div style={{ background:"#fff8e6", border:"1px solid #fbbf24", borderRadius:8, padding:"10px 14px", fontSize:12, color:"#92400e" }}>
            Receipt: <strong>{offer.mpesaReceiptNumber||"Processing..."}</strong>
          </div>
        </div>
      )}

      {step === "paid" && (
        <div style={{ background: "white", borderRadius: 14, padding: "40px 28px", textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
          <div style={{ width: 72, height: 72, background: "#d1fae5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 32 }}>?</div>
          <h3 style={{ fontSize: 20, fontWeight: 800, color: "#065f46", margin: "0 0 10px" }}>Loan Disbursed!</h3>
          <p style={{ color: "var(--text-secondary)", fontSize: 14 }}>Your loan of <strong style={{ color: "#0066cc" }}>{fmt(offer.selectedAmount)}</strong> has been sent to your account.</p>
        </div>
      )}
    </div>
  );
}











